import React, { useState } from "react";
import { Box } from "@mui/material";
import { IconArrowRight, IconProduct } from "../../utils/icons";

const SKU_REGEX = /^[A-Za-z0-9._-]+$/;
const SKU_MAX_LENGTH = 50;

// The input caps length at SKU_MAX_LENGTH, so only emptiness and the
// character set can fail here.
const validationMessage = (sku: string) => {
    if (!sku) return "Enter a SKU before adding it.";
    if (!SKU_REGEX.test(sku)) return "Letters, numbers, hyphens, underscores and periods only.";
    return "";
};

const labelCls = "block text-[12px] font-semibold text-text-2 uppercase tracking-[0.3px] mb-[7px]";
const inputWrapCls =
    "flex items-center gap-[9px] px-[14px] py-[11px] border border-border-token rounded-[10px] bg-surface transition-colors focus-within:border-accent";
const inputCls =
    "field-input flex-1 appearance-none border-0 bg-transparent outline-none text-[13.5px] text-text-1 min-w-0 placeholder:text-muted";
const chipCls =
    "inline-flex items-center gap-[5px] px-[10px] py-[5px] rounded-[7px] bg-surface-2 border border-border-token font-mono text-[12px] text-text-1";

interface Props {
    value: string[];
    onChange: (skus: string[]) => void;
    disabled?: boolean;
}

export function SkuPdpLinkEditor({ value, onChange, disabled = false }: Props) {
    const [draft, setDraft] = useState("");
    const [error, setError] = useState("");

    const add = () => {
        const sku = draft.trim();
        const message = validationMessage(sku);
        if (message) {
            setError(message);
            return;
        }
        setError("");
        setDraft("");
        if (!value.includes(sku)) onChange([...value, sku]);
    };

    return (
        <Box>
            <span className={labelCls}>SKUs (optional)</span>
            <Box className={inputWrapCls}>
                <IconProduct width={15} height={15} stroke="#B5AFA5" />
                <input
                    className={inputCls}
                    placeholder="Add a SKU"
                    maxLength={SKU_MAX_LENGTH}
                    disabled={disabled}
                    value={draft}
                    onChange={(e) => {
                        setDraft(e.target.value);
                        if (error) setError("");
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            add();
                        }
                    }}
                    onBlur={() => draft.trim() && add()}
                />
                <button
                    type="button"
                    onClick={add}
                    disabled={disabled || !draft.trim()}
                    className="shrink-0 inline-flex items-center justify-center h-[20px] w-[20px] rounded-[6px] text-accent-text transition-colors hover:bg-tint disabled:opacity-40 disabled:hover:bg-transparent"
                >
                    <IconArrowRight width={18} height={18} stroke="currentColor" />
                    <span className="sr-only">Add SKU</span>
                </button>
            </Box>
            {error && <p className="text-[12px] text-[var(--down)] mt-[6px]">{error}</p>}
            {value.length > 0 && (
                <Box className="flex flex-wrap gap-[6px] mt-[9px]">
                    {value.map((sku) => (
                        <span key={sku} className={chipCls}>
                            {sku}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => onChange(value.filter((s) => s !== sku))}
                                    className="text-muted hover:text-text-1 transition-colors leading-none"
                                >
                                    <span aria-hidden="true">×</span>
                                    <span className="sr-only">Remove {sku}</span>
                                </button>
                            )}
                        </span>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default SkuPdpLinkEditor;
